import axios from "axios";
import { getCurrentApiBaseUrl } from "./config";
import { getAccessToken, getRefreshToken, setTokens, clearTokens, getApiToken } from "../utils/tokenStorage";
import { CapacitorHttp } from '@capacitor/core';
import { Capacitor } from '@capacitor/core';

// Ermittle den korrekten Authorization-Header basierend auf verfügbaren Tokens
const getAuthHeader = () => {
  // Prüfe zuerst ob ein API-Token vorhanden ist (QR-Code Login)
  const apiToken = getApiToken();
  if (apiToken) {
    return `Token ${apiToken}`;
  }

  // Ansonsten nutze den JWT Access Token
  const accessToken = getAccessToken();
  if (accessToken) {
    return `Bearer ${accessToken}`;
  }

  return null;
};

// Helper to build URL with query params
const buildUrlWithParams = (baseUrl, url, params) => {
  let fullUrl = `${baseUrl}${url}`;
  if (params && Object.keys(params).length > 0) {
    const queryString = new URLSearchParams(params).toString();
    fullUrl = `${fullUrl}?${queryString}`;
  }
  return fullUrl;
};

// Helper to make HTTP requests using native or web client
const makeRequest = async (config) => {
  const baseURL = getCurrentApiBaseUrl();
  const url = buildUrlWithParams(baseURL, config.url, config.params);

  if (Capacitor.isNativePlatform()) {
    // Use native HTTP for mobile
    console.log('Native HTTP request:', config.method, url);
    const options = {
      url,
      method: config.method || 'GET',
      headers: config.headers || {},
    };

    if (config.data) {
      // CapacitorHttp needs the data as a JSON string for Android
      options.data = typeof config.data === 'string' ? config.data : JSON.stringify(config.data);
    }

    try {
      const response = await CapacitorHttp.request(options);
      return {
        data: response.data,
        status: response.status,
        headers: response.headers,
      };
    } catch (error) {
      // Transform Capacitor error to axios-like error
      throw {
        response: {
          status: error.status || 0,
          data: error.data,
        },
        message: error.message,
        config,
      };
    }
  } else {
    // Use axios for web
    return axios({
      ...config,
      baseURL,
    });
  }
};

// Erstelle eine Axios-Instanz ohne feste baseURL (für Web)
const apiClient = axios.create({
  headers: {
    "Content-Type": "application/json",
  }
});

// Wrapper for making requests
apiClient.request = makeRequest;

// Override GET method to use native HTTP on mobile
const originalGet = apiClient.get.bind(apiClient);
apiClient.get = async (url, config = {}) => {
  if (Capacitor.isNativePlatform()) {
    const authHeader = getAuthHeader();
    const headers = {
      ...config.headers,
    };
    if (authHeader) {
      headers.Authorization = authHeader;
    }
    return makeRequest({
      url,
      method: 'GET',
      headers,
      ...config
    });
  } else {
    return originalGet(url, config);
  }
};

// Override POST method to use native HTTP on mobile
const originalPost = apiClient.post.bind(apiClient);
apiClient.post = async (url, data, config = {}) => {
  if (Capacitor.isNativePlatform()) {
    const authHeader = getAuthHeader();
    const headers = {
      'Content-Type': 'application/json',  // ✅ WICHTIG: Content-Type setzen!
      ...config.headers,
    };
    if (authHeader) {
      headers.Authorization = authHeader;
    }
    return makeRequest({
      url,
      method: 'POST',
      headers,
      data,
      ...config
    });
  } else {
    return originalPost(url, data, config);
  }
};

// Override PUT method to use native HTTP on mobile
const originalPut = apiClient.put.bind(apiClient);
apiClient.put = async (url, data, config = {}) => {
  if (Capacitor.isNativePlatform()) {
    const authHeader = getAuthHeader();
    const headers = {
      'Content-Type': 'application/json',  // ✅ WICHTIG: Content-Type setzen!
      ...config.headers,
    };
    if (authHeader) {
      headers.Authorization = authHeader;
    }
    return makeRequest({
      url,
      method: 'PUT',
      headers,
      data,
      ...config
    });
  } else {
    return originalPut(url, data, config);
  }
};

// Override PATCH method to use native HTTP on mobile
const originalPatch = apiClient.patch.bind(apiClient);
apiClient.patch = async (url, data, config = {}) => {
  if (Capacitor.isNativePlatform()) {
    const authHeader = getAuthHeader();
    const headers = {
      'Content-Type': 'application/json',  // ✅ WICHTIG: Content-Type setzen!
      ...config.headers,
    };
    if (authHeader) {
      headers.Authorization = authHeader;
    }
    return makeRequest({
      url,
      method: 'PATCH',
      headers,
      data,
      ...config
    });
  } else {
    return originalPatch(url, data, config);
  }
};

// Override DELETE method to use native HTTP on mobile
const originalDelete = apiClient.delete.bind(apiClient);
apiClient.delete = async (url, config = {}) => {
  if (Capacitor.isNativePlatform()) {
    const authHeader = getAuthHeader();
    const headers = {
      ...config.headers,
    };
    if (authHeader) {
      headers.Authorization = authHeader;
    }
    return makeRequest({
      url,
      method: 'DELETE',
      headers,
      ...config
    });
  } else {
    return originalDelete(url, config);
  }
};

// Flag um mehrfache Refresh-Requests zu verhindern
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Füge den Authorization-Header und dynamische baseURL zu jedem Request hinzu
apiClient.interceptors.request.use(
  (config) => {
    // Dynamically set baseURL on each request
    config.baseURL = getCurrentApiBaseUrl();

    const authHeader = getAuthHeader();
    if (authHeader) {
      config.headers.Authorization = authHeader;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response-Interceptor für automatischen Token-Refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Wenn 401 und noch nicht versucht zu refreshen
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Warte auf den laufenden Refresh
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = getRefreshToken();

      if (!refreshToken) {
        await clearTokens();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const API_BASE_URL = getCurrentApiBaseUrl();
        const response = await axios.post(`${API_BASE_URL}/auth/token/refresh/`, {
          refresh: refreshToken,
        });

        const { access } = response.data;
        await setTokens(access, refreshToken);

        apiClient.defaults.headers.common['Authorization'] = `Bearer ${access}`;
        originalRequest.headers.Authorization = `Bearer ${access}`;

        processQueue(null, access);
        isRefreshing = false;

        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;
        await clearTokens();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;